// The ledger and the reports: the journal with every entry's lines, the trial balance, profit and
// loss, balance sheet, cash flow, job costing (the owner's, Q24), a manual entry, closing a month
// once its checks pass, and the accounting system the export goes to. Reversing an entry and the
// accountant pack are in Corrections.tsx (gap #17).

import { useEffect, useMemo, useState } from 'react';
import { holdsAny } from '../shared/may.ts';
import { client } from '../api.ts';
import { AccountantPack, Months, ReverseSheet } from './Corrections.tsx';
import { Connections } from './Connections.tsx';
import { ledgerReturnUntold } from './ledger-connect.ts';
import {
  notReversibleWhy,
  PACK_PERMS,
  REOPEN_PERMS,
  REVERSE_PERMS,
  reversible,
} from './corrections-model.ts';
import type { BooksProps } from './props.ts';
import { dayText, entryKind, monthName, monthOf, parseMoney, previousMonth, todayIn } from './model.ts';
import type {
  Account,
  BalanceSheet,
  CloseChecks,
  JournalEntry,
  ProfitAndLoss,
  ReportRow,
  TrialBalance,
} from './types.ts';
import { ClosedMonthHint, Empty, Loading, Money, Panel, useAct, useQuery } from './ui.tsx';
import { scrollFocus } from '../shared/scrollFocus.ts';
import { useDesign } from '../blueprint/design.ts';
import { Moved } from '../blueprint/Moved.tsx';
import { useDocFocus } from '../shared/doc-link.ts';
import { BEFORE_THE_SWITCH, useBringingOver } from '../migration/bringing-over.ts';

/** Newest first by posting date, then by entry number (JE-14 after JE-6). */
const byDateThenNumber = (a: JournalEntry, b: JournalEntry) =>
  b.postingDate.localeCompare(a.postingDate) ||
  Number(b.number.replace(/\D/g, '')) - Number(a.number.replace(/\D/g, ''));

type Tab = 'journal' | 'trial' | 'pl' | 'bs' | 'cash' | 'jobs' | 'entry' | 'close' | 'connect' | 'pack';

/**
 * Under books with nothing in them while the company is still bringing ServiceTitan over: why (history
 * fix 4, 22 Oct). Flowology's books start at the switch; nothing otherwise.
 */
function BeforeTheSwitch() {
  return useBringingOver() ? <p className="muted small">{BEFORE_THE_SWITCH}</p> : null;
}

function Rows({ rows, total, label }: { rows: ReportRow[]; total: number; label: string }) {
  return (
    <>
      {rows.map((r) => (
        <tr key={r.accountId}>
          <td>
            <span className="muted">{r.code}</span> {r.name}
          </td>
          <td className="num">
            <Money cents={r.amountCents} />
          </td>
        </tr>
      ))}
      <tr className="sum">
        <td>{label}</td>
        <td className="num">
          <Money cents={total} />
        </td>
      </tr>
    </>
  );
}

function Journal({
  mayReverse,
  toast,
  focus,
}: {
  mayReverse: boolean;
  toast: (m: string) => void;
  /** A journal entry a document link opened: the dates move to its month, and it opens. */
  focus?: string;
}) {
  const [from, setFrom] = useState(monthOf(todayIn())[0]);
  const [to, setTo] = useState(todayIn());
  const [open, setOpen] = useState<string | null>(focus ?? null);
  const [found, setFound] = useState(!focus);
  useEffect(() => {
    if (!focus) return;
    client
      .call<JournalEntry>('journal.get', { id: focus })
      .then((e) => {
        const [start, end] = monthOf(e.postingDate);
        setFrom(start);
        setTo(end);
        setFound(true);
      })
      .catch(() => {
        toast('That journal entry can’t be opened here.');
        setFound(true);
      });
  }, [focus, toast]);
  const j = useQuery<{ entries: JournalEntry[] }>('journal.list', found ? { from, to, limit: 200 } : null);
  useDocFocus(focus, !!j.data && found);
  // journal.reverse: the entry being reversed, on the money sheet
  const [reversing, setReversing] = useState<JournalEntry | null>(null);
  return (
    <Panel
      title="Journal"
      aside={
        <div className="dates">
          <input
            className="field"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            aria-label="From"
          />
          <input
            className="field"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            aria-label="To"
          />
        </div>
      }
    >
      {!j.data ? (
        <Loading error={j.error} what="the journal" kind="table" retry={() => void j.reload()} />
      ) : j.data.entries.length === 0 ? (
        <>
          <Empty>Nothing posted in these dates.</Empty>
          <BeforeTheSwitch />
        </>
      ) : (
        <ul className="rows">
          {[...j.data.entries].sort(byDateThenNumber).map((e) => {
            const amount = e.lines.reduce((s, l) => s + l.debitCents, 0);
            return (
              <li key={e.id} className="entry" data-doc={e.id}>
                <button
                  className="entry-row"
                  aria-expanded={open === e.id}
                  onClick={() => setOpen(open === e.id ? null : e.id)}
                >
                  <span className="muted">{e.number}</span>
                  <span>
                    {dayText(e.postingDate)}
                    {/* A late document posts in the first open month; both dates show (Q48, QA-263). */}
                    {e.documentDate && e.documentDate !== e.postingDate && (
                      <span className="muted small"> · document {dayText(e.documentDate)}</span>
                    )}
                  </span>
                  <span className="entry-memo">{e.memo}</span>
                  <span className="chip say">{entryKind(e.rule ?? e.source)}</span>
                  <Money cents={amount} className="num" />
                </button>
                {open === e.id && (
                  <div className="table-wrap" ref={scrollFocus}>
                    <table className="grid-table">
                      <tbody>
                        {e.lines.map((l) => (
                          <tr key={l.lineNo}>
                            <td>{l.debitCents ? 'Dr' : 'Cr'}</td>
                            <td>
                              <span className="muted">{l.accountCode}</span> {l.accountName}
                              {l.memo ? <span className="muted small"> · {l.memo}</span> : null}
                            </td>
                            <td className="num">{l.debitCents ? <Money cents={l.debitCents} /> : null}</td>
                            <td className="num">{l.creditCents ? <Money cents={l.creditCents} /> : null}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {mayReverse && reversible(e) ? (
                      <div className="row-actions">
                        <button type="button" className="btn" onClick={() => setReversing(e)}>
                          Reverse {e.number}
                        </button>
                      </div>
                    ) : (
                      notReversibleWhy(e) && <p className="muted small">{notReversibleWhy(e)}</p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {reversing && (
        <ReverseSheet
          entry={reversing}
          onClose={() => setReversing(null)}
          onReversed={(said) => {
            setReversing(null);
            toast(said);
            void j.reload();
          }}
        />
      )}
    </Panel>
  );
}

function Trial() {
  const t = useQuery<TrialBalance>('reports.trialBalance', {});
  if (!t.data)
    return (
      <Loading
        error={t.error}
        what="the trial balance"
        kind="table"
        cols={['Account', 'Debit', 'Credit']}
        retry={() => void t.reload()}
      />
    );
  return (
    <Panel
      title={`Trial balance · ${dayText(t.data.asOf)}`}
      aside={
        <span className={`chip say ${t.data.balanced ? 'ok' : 'warn'}`}>
          {t.data.balanced ? 'Balanced' : 'Out of balance'}
        </span>
      }
    >
      <div className="table-wrap" ref={scrollFocus}>
        <table className="grid-table">
          <thead>
            <tr>
              <th>Account</th>
              <th className="num">Debit</th>
              <th className="num">Credit</th>
            </tr>
          </thead>
          <tbody>
            {t.data.rows.map((r) => (
              <tr key={r.accountId}>
                <td>
                  <span className="muted">{r.code}</span> {r.name}
                </td>
                <td className="num">{r.debitCents ? <Money cents={r.debitCents} /> : null}</td>
                <td className="num">{r.creditCents ? <Money cents={r.creditCents} /> : null}</td>
              </tr>
            ))}
            <tr className="sum">
              <td>Total</td>
              <td className="num">
                <Money cents={t.data.totalDebitCents} />
              </td>
              <td className="num">
                <Money cents={t.data.totalCreditCents} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {t.data.totalDebitCents === 0 && <BeforeTheSwitch />}
    </Panel>
  );
}

function PL() {
  const [[from, to], setRange] = useState(monthOf(todayIn()));
  const p = useQuery<ProfitAndLoss>('reports.profitAndLoss', { from, to });
  return (
    <Panel
      title={`Profit and loss · ${monthName(from)}`}
      aside={
        <div className="seg">
          <button className="btn quiet" onClick={() => setRange(previousMonth(from))}>
            ‹ Earlier
          </button>
          <button className="btn quiet" onClick={() => setRange(monthOf(todayIn()))}>
            This month
          </button>
        </div>
      }
    >
      {!p.data ? (
        <Loading error={p.error} what="profit and loss" kind="table" retry={() => void p.reload()} />
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table report">
            <tbody>
              <Rows rows={p.data.income} total={p.data.incomeCents} label="Income" />
              <Rows rows={p.data.costOfSales} total={p.data.costOfSalesCents} label="Cost of sales" />
              <tr className="sum strong">
                <td>Gross profit</td>
                <td className="num">
                  <Money cents={p.data.grossProfitCents} />
                </td>
              </tr>
              <Rows rows={p.data.expenses} total={p.data.expensesCents} label="Expenses" />
              <tr className="sum strong">
                <td>Net profit</td>
                <td className="num">
                  <Money cents={p.data.netProfitCents} />
                </td>
              </tr>
            </tbody>
          </table>
          {!p.data.incomeCents && !p.data.costOfSalesCents && !p.data.expensesCents && <BeforeTheSwitch />}
        </div>
      )}
    </Panel>
  );
}

function BS() {
  const b = useQuery<BalanceSheet>('reports.balanceSheet', {});
  if (!b.data)
    return <Loading error={b.error} what="the balance sheet" kind="table" retry={() => void b.reload()} />;
  const d = b.data;
  return (
    <Panel
      title={`Balance sheet · ${dayText(d.asOf)}`}
      aside={
        <span className={`chip say ${d.balanced ? 'ok' : 'warn'}`}>
          {d.balanced ? 'Balances' : 'Does not balance'}
        </span>
      }
    >
      <div className="table-wrap" ref={scrollFocus}>
        <table className="grid-table report">
          <tbody>
            <Rows rows={d.assets} total={d.assetsCents} label="Assets" />
            <Rows rows={d.liabilities} total={d.liabilitiesCents} label="Liabilities" />
            <Rows
              rows={[
                ...d.equity,
                {
                  accountId: 're',
                  code: '',
                  name: 'Retained earnings',
                  amountCents: d.retainedEarningsCents,
                },
                {
                  accountId: 'ce',
                  code: '',
                  name: 'Earnings this year',
                  amountCents: d.currentEarningsCents,
                },
              ]}
              total={d.equityCents}
              label="Equity"
            />
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function CashFlow() {
  const [from, to] = monthOf(todayIn());
  const c = useQuery<{
    netProfitCents: number;
    operating: ReportRow[];
    investing: ReportRow[];
    financing: ReportRow[];
    operatingCents: number;
    investingCents: number;
    financingCents: number;
    netChangeCents: number;
    openingCashCents: number;
    closingCashCents: number;
    ties: boolean;
  }>('reports.cashFlow', { from, to });
  if (!c.data) return <Loading error={c.error} what="cash flow" kind="table" retry={() => void c.reload()} />;
  const d = c.data;
  return (
    <Panel
      title={`Cash flow · ${monthName(from)}`}
      aside={
        <span className={`chip say ${d.ties ? 'ok' : 'warn'}`}>
          {d.ties ? 'Ties to the bank' : 'Does not tie'}
        </span>
      }
    >
      <div className="table-wrap" ref={scrollFocus}>
        <table className="grid-table report">
          <tbody>
            <tr>
              <td>Net profit</td>
              <td className="num">
                <Money cents={d.netProfitCents} />
              </td>
            </tr>
            <Rows rows={d.operating} total={d.operatingCents} label="From operations" />
            <Rows rows={d.investing} total={d.investingCents} label="Investing" />
            <Rows rows={d.financing} total={d.financingCents} label="Financing" />
            <tr className="sum strong">
              <td>Change in cash</td>
              <td className="num">
                <Money cents={d.netChangeCents} />
              </td>
            </tr>
            <tr>
              <td>Cash at the start · end</td>
              <td className="num">
                <Money cents={d.openingCashCents} /> → <Money cents={d.closingCashCents} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function JobCosting() {
  const [from, to] = monthOf(todayIn());
  const j = useQuery<{
    rows: {
      jobId: string;
      jobNumber: string;
      revenueCents: number;
      costOfSalesCents: number;
      /** Paid labour with its burden: in gross profit, so it's in the cost here too. */
      labourCents?: number;
      grossProfitCents: number;
      marginPct: number | null;
    }[];
    grossProfitCents: number;
  }>('reports.jobCosting', { from, to });
  if (!j.data)
    return <Loading error={j.error} what="job costing" kind="table" retry={() => void j.reload()} />;
  return (
    <Panel title={`Job costing · ${monthName(from)}`} aside={<span className="chip say">Owner only</span>}>
      {j.data.rows.length === 0 ? (
        <>
          <Empty>No job revenue this month.</Empty>
          <BeforeTheSwitch />
        </>
      ) : (
        <div className="table-wrap" ref={scrollFocus}>
          <table className="grid-table">
            <thead>
              <tr>
                <th>Job</th>
                <th className="num">Revenue</th>
                <th className="num">Cost</th>
                <th className="num">Gross profit</th>
                <th className="num">Margin</th>
              </tr>
            </thead>
            <tbody>
              {j.data.rows.map((r) => (
                <tr key={r.jobId}>
                  <td>#{r.jobNumber}</td>
                  <td className="num">
                    <Money cents={r.revenueCents} />
                  </td>
                  <td className="num">
                    <Money cents={r.costOfSalesCents + (r.labourCents ?? 0)} />
                  </td>
                  <td className="num">
                    <Money cents={r.grossProfitCents} />
                  </td>
                  <td className="num">{r.marginPct === null ? '—' : `${Math.round(r.marginPct)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

interface DraftLine {
  key: number;
  accountId: string;
  debit: string;
  credit: string;
}

function NewEntry({ toast }: { toast: (m: string) => void }) {
  const accounts = useQuery<{ accounts: Account[] }>('accounts.list', {});
  const act = useAct(toast, () => undefined);
  const [date, setDate] = useState(todayIn());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([
    { key: 1, accountId: '', debit: '', credit: '' },
    { key: 2, accountId: '', debit: '', credit: '' },
  ]);
  const totals = useMemo(() => {
    const d = lines.reduce((s, l) => s + (parseMoney(l.debit) ?? 0), 0);
    const c = lines.reduce((s, l) => s + (parseMoney(l.credit) ?? 0), 0);
    return { d, c };
  }, [lines]);
  const set = (k: number, patch: Partial<DraftLine>) =>
    setLines(lines.map((l) => (l.key === k ? { ...l, ...patch } : l)));
  const ready =
    memo.trim() &&
    totals.d > 0 &&
    totals.d === totals.c &&
    lines.every((l) => !(parseMoney(l.debit) || parseMoney(l.credit)) || l.accountId);
  const submit = async (moveToOpen = false) => {
    const r = await act.run<{ number: string; moved: boolean; postingDate: string }>('journal.post', {
      date,
      memo: memo.trim(),
      ...(moveToOpen ? { postInFirstOpenPeriod: true } : {}),
      lines: lines
        .filter((l) => l.accountId && (parseMoney(l.debit) || parseMoney(l.credit)))
        .map((l) => ({
          accountId: l.accountId,
          ...(parseMoney(l.debit) ? { debitCents: parseMoney(l.debit)! } : {}),
          ...(parseMoney(l.credit) ? { creditCents: parseMoney(l.credit)! } : {}),
        })),
    });
    if (r) {
      toast(
        r.moved
          ? `Posted ${r.number} on ${dayText(r.postingDate)}, the first open day.`
          : `Posted ${r.number}.`,
      );
      setMemo('');
      setLines([
        { key: 1, accountId: '', debit: '', credit: '' },
        { key: 2, accountId: '', debit: '', credit: '' },
      ]);
    }
  };
  if (!accounts.data)
    return (
      <Loading error={accounts.error} what="the chart of accounts" retry={() => void accounts.reload()} />
    );
  return (
    <Panel
      title="New journal entry"
      aside={
        <span className={`chip say ${totals.d === totals.c && totals.d > 0 ? 'ok' : 'warn'}`}>
          {totals.d === totals.c ? (
            'Balanced'
          ) : (
            <>
              Off by <Money cents={totals.d - totals.c} />
            </>
          )}
        </span>
      }
    >
      <div className="fgrid">
        <label>
          <span className="flabel">Date</span>
          <input className="field" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <ClosedMonthHint date={date} />
        </label>
        <label className="wide">
          <span className="flabel">Memo</span>
          <input
            className="field"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="What this entry is for"
          />
        </label>
      </div>
      <ul className="entry-lines">
        {lines.map((l) => (
          <li key={l.key} className="entry-line">
            <select
              className="field"
              value={l.accountId}
              onChange={(e) => set(l.key, { accountId: e.target.value })}
              aria-label="Account"
            >
              <option value="">Account…</option>
              {accounts
                .data!.accounts.filter((a) => a.active)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {a.name}
                  </option>
                ))}
            </select>
            <input
              className="field"
              inputMode="decimal"
              placeholder="Debit"
              value={l.debit}
              onChange={(e) => set(l.key, { debit: e.target.value, credit: '' })}
              aria-label="Debit"
            />
            <input
              className="field"
              inputMode="decimal"
              placeholder="Credit"
              value={l.credit}
              onChange={(e) => set(l.key, { credit: e.target.value, debit: '' })}
              aria-label="Credit"
            />
          </li>
        ))}
      </ul>
      <div className="row-actions">
        <button
          className="btn quiet"
          onClick={() => setLines([...lines, { key: Date.now(), accountId: '', debit: '', credit: '' }])}
        >
          Add a line
        </button>
        <button className="btn primary" disabled={!ready || act.busy} onClick={() => void submit()}>
          Post
        </button>
        <button
          className="btn"
          disabled={!ready || act.busy}
          onClick={() => void submit(true)}
          title="If the date is in a closed month, post on the first open day"
        >
          Post in the first open month
        </button>
      </div>
    </Panel>
  );
}

function Close({ toast }: { toast: (m: string) => void }) {
  const [, end] = previousMonth(todayIn());
  const c = useQuery<CloseChecks>('periods.closeChecks', { date: end });
  const act = useAct(toast, () => c.reload());
  const [reviewed, setReviewed] = useState(false);
  if (!c.data) return <Loading error={c.error} what="the month-end checks" retry={() => void c.reload()} />;
  const p = c.data.period;
  return (
    <Panel
      title={`Close ${monthName(p.startDate)}`}
      aside={
        <span className={`chip say ${p.status === 'closed' ? 'ok' : ''}`}>
          {p.status === 'closed' ? 'Closed' : 'Open'}
        </span>
      }
    >
      <ul className="checks">
        {c.data.checks.map((k) => (
          <li key={k.key} className={k.ok ? 'ok' : k.blocking ? 'bad' : 'warn'}>
            <span aria-hidden="true">{k.ok ? '✓' : k.blocking ? '✕' : '!'}</span>
            <span>
              {k.label}
              {k.detail ? <span className="muted small"> · {k.detail}</span> : null}
            </span>
          </li>
        ))}
      </ul>
      {p.status === 'open' && (
        <>
          <label className="checkrow">
            <input type="checkbox" checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} /> I
            reviewed the aging and the accruals
          </label>
          <div className="row-actions">
            <button
              className="btn primary"
              disabled={!reviewed || !c.data.ready || act.busy}
              onClick={() =>
                void act.run(
                  'periods.close',
                  { date: end, reviewedAgingAndAccruals: true },
                  `${monthName(p.startDate)} is closed.`,
                )
              }
            >
              Close the month
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

export function Ledger({ held, toast, timeZone, focus }: BooksProps) {
  // job costing (reports.jobCosting): finance.margins.view, the owner's or a role of theirs that gives it
  const owner = held.includes('finance.margins.view');
  // Back from QuickBooks or Xero: the accounting system; a journal entry's link: the journal.
  const [tab, setTab] = useState<Tab>(focus ? 'journal' : ledgerReturnUntold() ? 'connect' : 'pl');
  // Blueprint: the accounting system is Setup › Connected systems' now (the setup audit), and this tab
  // says so; coming back from the provider it still shows here, to say how connecting went
  const bp = useDesign() === 'blueprint';
  const [returned] = useState(ledgerReturnUntold);
  const TABS: [Tab, string][] = [
    ['pl', 'Profit and loss'],
    ['bs', 'Balance sheet'],
    ['cash', 'Cash flow'],
    ['trial', 'Trial balance'],
    ...(owner ? ([['jobs', 'Job costing']] as [Tab, string][]) : []),
    ['journal', 'Journal'],
    ['entry', 'New entry'],
    ['close', 'Close a month'],
    ...(holdsAny(held, PACK_PERMS) ? ([['pack', 'Accountant pack']] as [Tab, string][]) : []),
    ['connect', 'Accounting system'],
  ];
  return (
    <div className="books-page">
      <div className="page-head">
        <div className="seg wrap" role="tablist" aria-label="Reports">
          {TABS.map(([t, label]) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              className={`btn quiet${tab === t ? ' on' : ''}`}
              onClick={() => setTab(t)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'journal' && (
        <Journal mayReverse={holdsAny(held, REVERSE_PERMS)} toast={toast} {...(focus ? { focus } : {})} />
      )}
      {tab === 'trial' && <Trial />}
      {tab === 'pl' && <PL />}
      {tab === 'bs' && <BS />}
      {tab === 'cash' && <CashFlow />}
      {tab === 'jobs' && owner && <JobCosting />}
      {tab === 'entry' && <NewEntry toast={toast} />}
      {tab === 'close' && (
        <>
          <Close toast={toast} />
          <Months mayReopen={holdsAny(held, REOPEN_PERMS)} toast={toast} />
        </>
      )}
      {tab === 'pack' && <AccountantPack />}
      {tab === 'connect' && bp && (
        <Moved what="The accounting system" to="Setup › Connected systems" href="#/settings/connections" />
      )}
      {tab === 'connect' && (!bp || returned) && (
        <Connections toast={toast} {...(timeZone ? { timeZone } : {})} />
      )}
    </div>
  );
}
